﻿import { parseColor255 } from '@fe/core';
import { DeckOverlayProps, IconSize, RangeType, SubstituteIconType } from '@fe/flight-layer';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { MessageType } from '../../../../../services/actor-messages';
import { EventBus, useEvent } from '../../../../contexts/event-bus';
import WorkspaceItemContext from '../../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { useFlightWorker } from '../../../../hooks/use-flight-worker';
import { useMapIsLight } from '../../../../hooks/use-map';
import { useSafeEffect } from '../../../../hooks/use-safe-effect';
import { updateFlightsRoutes, updateSelectedFlights } from '../../../../state/slices/workspace-slice';
import { store } from '../../../../state/store';
import { useWorkspace, useWorkspaceView } from '../../hooks/use-view';
import { FlightAlerts } from './FlightAlerts';
import { WorkspaceUdViewSync } from './WorkspaceUdViewSync';
import FlightFollowMe from './FlightFollowMe';
import { FlightForward } from './FlightForward';
import { FlightIndividual } from './FlightIndividual';
import { FlightRoutes } from './FlightRoutes';
import FlightSelection from './FlightSelection';
import {UDViewHistoryColorSource} from "../../../../../udView/udview";

export default function FlightLayer() {
    const dispatch = useAppDispatch();
    const context = React.useContext(WorkspaceItemContext);
    const cityPair = useWorkspaceView((x) => x.plane.settings.cityPair);
    const focusedWorkspaceId = useAppSelector((x) => x.workspace.selectedId);
    const ringsSettings = useWorkspaceView((x) => x.plane.range);
    const vectorSettings = useWorkspaceView((x) => x.plane.vector);
    const planeSettings = useWorkspaceView((x) => x.plane.settings);
    const iconSettings = useWorkspace((x) => x.view.plane.icon);
    const tagSettings = useWorkspaceView((x) => x.plane.tag);
    const useGlobeView = useWorkspaceView((x) => x.map.useGlobeView);
    const routeFlownSettings = useWorkspace((x) => x.flights.settings.routeFlown);
    const selectedFlightShowTag = useWorkspace((x) => x.flights.settings.showTags);
    const focus = useWorkspace((x) => x.flights.settings.focus);
    const followedFlightId = useWorkspace((x) => x.flights.followed);
    const individualRouteFlown = useAppSelector((x) => x.workspace.individual.routeFlown);
    const mapIsLight = useMapIsLight();
    const worker = useFlightWorker();
    const globalSettings = useAppSelector(
        (state) => ({
            defaultAircraftParameters: state.settings.defaultAircraftParameters,
            aircraftTypeParameters: state.settings.aircraftParameters,
            colorizeRouteFlown: state.settings.colorizeRouteFlown,
            aircraftMonitorStatusLayer: state.settings.showAircraftMonitorStatus
        }),
        shallowEqual
    );
    const coastModeDebugOverlay = useAppSelector((x) => x.settings.coastModeDebugOverlay);

    const props = React.useMemo<DeckOverlayProps>(
        () => ({
            windParticles: {
                isEnabled: false,
                colorLight: [210, 210, 210, 200],
                colorDark: [20, 20, 20, 180],
                currentColor: mapIsLight ? [20, 20, 20, 180] : [210, 210, 210, 200],
                opacity: 0.5,
                data: null
            },
            flights: {
                visible: planeSettings.show,
                active: {
                    isCoastMode: planeSettings.enableCoastMode,
                    coastDebugOverlay: coastModeDebugOverlay,
                    showProjectedPoints: planeSettings.showProjectedPoints,
                    defaultFlightColor: [85, 133, 245],
                    historyColor: parseColor255(planeSettings.historyColor ?? 'rgba(0,0,0,0'),
                    hiddenColor: [0, 0, 0, 0],
                    highlightColor: mapIsLight ? [0xaa, 0xaa, 0xaa] : [0xff, 0xff, 0xff],
                    showShadow: iconSettings.drawShadow && !useGlobeView,
                    visible: planeSettings.showActive,
                    historySmoothing: true,
                    historyModern: planeSettings.historyModern,
                    historyUseAircraftColor: planeSettings.historyColorSource === UDViewHistoryColorSource.aircraft,
                    iconSize: iconSettings.size as unknown as IconSize,
                    substituteIconType: iconSettings.type as unknown as SubstituteIconType,
                    iconZoomThreshold: iconSettings.zoomLevel, // if less flight icons are circles
                    iconSizeZoomThreshold: 4, // when icon changes size
                    isMonitorStatus: globalSettings.aircraftMonitorStatusLayer,
                    showDiversions: planeSettings.showDiversionLines,
                    diversionLineOldColor: [255, 0, 0, 60],
                    diversionLineNewColor: [0, 255, 0, 255],
                    showOnlySelected: focus,
                    followedFlightId: followedFlightId,
                    routeFlownEnabledFlights: individualRouteFlown,
                    tags: {
                        color: parseColor255(tagSettings.textColor ?? 'rgba(0, 0, 0, 0)'),
                        leaderColor: parseColor255(
                            (mapIsLight ? tagSettings.backgroundColor : tagSettings.textColor) ?? 'rgba(0, 0, 0, 0)'
                        ),
                        background: parseColor255(tagSettings.backgroundColor ?? 'rgba(0, 0, 0, 0)'),
                        outlineMode: tagSettings.outlineMode,
                        outlineColor: parseColor255(tagSettings.outlineColor ?? 'rgba(0, 0, 0, 0)'),
                        autoTag: tagSettings.enableAutoTag,
                        hideLeader: tagSettings.disableTagLeader,
                        drawBackground: tagSettings.showLabelBackground,
                        minZoom: tagSettings.zoomLevel,
                        hasFlightDetailsTag: selectedFlightShowTag // Decides if selected flights should show tags
                    },
                    tooltipStyle: {
                        strong: {
                            fontWeight: 'bold'
                        },
                        margin: '10px',
                        padding: '5px',
                        color: '#3f3f3f',
                        backgroundColor: '#fff',
                        fontSize: '0.8em'
                    },
                    rings: {
                        isVisible: ringsSettings.isVisible,
                        range: ringsSettings.factor,
                        type: ringsSettings.type as unknown as RangeType,
                        color: parseColor255(ringsSettings.color ?? '#30d4fc55'),
                        lineColor: parseColor255(ringsSettings.lineColor ?? '#12cafc')
                    },
                    vectors: {
                        isVisible: vectorSettings.isVisible,
                        lineColor: parseColor255(vectorSettings.lineColor ?? '#12cafc'),
                        range: vectorSettings.factor,
                        type: vectorSettings.type as unknown as RangeType
                    }
                },
                scheduled: {
                    visible: planeSettings.showScheduled,
                    color: parseColor255('#ebebc8')
                },
                planned: {
                    visible: planeSettings.showPlanned,
                    color: parseColor255('#00dc00')
                },
                parked: {
                    visible: planeSettings.showParked
                },
                groundVehicles: {
                    visible: planeSettings.showGroundVehicles
                }
            },
            smoothingParams: {
                iteration: 2,
                factor: 0.75,
                dimension: 2
            },
            cityPair: cityPair,
            globeView: {
                useGlobeView: useGlobeView
            },
            routeFlown: {
                shouldColorize: globalSettings.colorizeRouteFlown,
                isVisible: routeFlownSettings.show
            },
            aircraftParameters: {
                defaultParameters: globalSettings.defaultAircraftParameters,
                aircraftTypeParameters: globalSettings.aircraftTypeParameters
            }
        }),
        [
            planeSettings,
            useGlobeView,
            globalSettings,
            vectorSettings,
            ringsSettings,
            iconSettings,
            focus,
            tagSettings,
            mapIsLight,
            routeFlownSettings,
            selectedFlightShowTag,
            followedFlightId,
            coastModeDebugOverlay,
            individualRouteFlown,
            cityPair
        ]
    );

    useSafeEffect(() => {
        context.map?.addPlugin(context.flightLayerFacade);
        const state = store.getState();
        const individual = state.workspace.individual;
        const selected = state.workspace.items[context.id].flights.selected;

        // Grab all the routes displayed and get latest routes at the start of this component tree
        // This is to that if we navigate to some other page the updates will stop because this component will be unmounted
        // TODO: I think we are OK to keep routes both for selected and individual flights in redux and update them in worker-client instead of here
        // The only tricky part is that the layer facade code is more efficient if you only give it what changed and not all the routes
        // and if we patched route flown every update that would be a lot of wasted time, unless we find a smart way to track changes.
        const getRoutesForFlights = Array.from(
            new Set([...individual.flightPath, ...individual.routeFlown, ...selected.map((x) => x.details.id)])
        );
        if (getRoutesForFlights.length > 0) {
            worker.actor.sendAsync({ type: MessageType.getRoutesForFlights, data: getRoutesForFlights }).then((response) => {
                for (const routes of response) {
                    context.flightLayerFacade.routes.upsertFlight(routes.flightId, routes);
                }
                dispatch(updateFlightsRoutes(response));
            });
        }

        return () => {
            context.map?.removePlugin(context.flightLayerFacade);
        };
    }, [worker]);

    useSafeEffect(() => {
        const windParticles = context.flightLayerFacade.deckPlugin.props?.windParticles;
        context.flightLayerFacade.deckPlugin.props = props;
        if (windParticles) {
            context.flightLayerFacade.deckPlugin.props.windParticles = windParticles;
        }
        context.flightLayerFacade.deckPlugin.updateLayers();
    }, [context, props]);

    useEvent(
        'select-flight',
        React.useCallback((e) => context.flightLayerFacade.selection.selectFlight(e.flightId, e.options), [context])
    );

    useEvent(
        'unselect-flight',
        React.useCallback((e) => context.flightLayerFacade.selection.deselectFlight(e.flightId, e.options), [context])
    );

    // Selection
    React.useEffect(() => {
        return context.flightLayerFacade.selection.on(async (e) => {
            const addDetails = await worker.actor.sendAsync({
                type: MessageType.updateFlightSelection,
                data: { addToSelection: e.selected, removeFromSelection: e.unselected }
            });

            dispatch(updateSelectedFlights({ add: addDetails, remove: e.unselected }));
            context.flightLayerFacade.deckPlugin.updateLayers({ shouldArrangeTags: true });

            if (addDetails && context.id === focusedWorkspaceId) {
                context.flightLayerFacade.routes.upsertFlight(addDetails.details.id, addDetails.routes);
                self.setTimeout(() => {
                    EventBus.fire('flight-selected', { details: addDetails.details, options: e.options });
                }, 25);
            }

            const state = store.getState();
            const individual = state.workspace.individual;

            for (const id of e.unselected) {
                context.flightLayerFacade.routes.removeFlight(id, {
                    keep: {
                        flightPlan: individual.flightPath.includes(id),
                        flown: individual.routeFlown.includes(id)
                    }
                });
            }
        }).unsubscribe;
    }, [dispatch, worker, focusedWorkspaceId, context]);

    return (
        <>
            <FlightSelection />
            <FlightRoutes />
            <FlightIndividual />
            <FlightFollowMe />
            <FlightAlerts />
            <FlightForward />
            <WorkspaceUdViewSync />
        </>
    );
}
